import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';
import {
  guidelinePipelineApi,
  guidelinesApi,
  PipelineTaskStatus,
} from '../../api';
import { INFINIWELL_ORG_ID } from '../../common/constants';


const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface CreateGuidelineDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ProcessingStage =
  | 'idle'
  | 'uploading'
  | 'pipeline_submitting'
  | 'pipeline_processing'
  | 'uploading_doc'
  | 'uploading_sections'
  | 'uploading_chunks'
  | 'uploading_embeddings'
  | 'complete'
  | 'error';

const SCOPE_OPTIONS = ['International', 'Regional', 'National'] as const;
type GuidelineScope = typeof SCOPE_OPTIONS[number];

interface GuidelineFormData {
  guideline_docs_id: string;
  title: string;
  description: string;
  source_org: string;
  author: string;
  language: string;
  version_label: string;
  published_date: string;
  image_url: string;
  url: string;
  jurisdiction: string;
  scope: GuidelineScope;
}

const CreateGuidelineDialog: React.FC<CreateGuidelineDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<GuidelineFormData>({
    guideline_docs_id: '',
    title: '',
    description: '',
    source_org: '',
    author: '',
    language: 'en',
    version_label: '',
    published_date: new Date().toISOString().split('T')[0],
    image_url: '',
    url: '',
    jurisdiction: 'USA',
    scope: 'National',
  });

  const [stage, setStage] = useState<ProcessingStage>('idle');
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const ext = file.name.split('.').pop()?.toLowerCase();
      
      if (ext !== 'json' && ext !== 'pdf') {
        setError('Please select a JSON or PDF file');
        return;
      }

      setSelectedFile(file);
      setError(null);

      if (!formData.guideline_docs_id) {
        const generatedId = uuidv4();
        setFormData(prev => ({ ...prev, guideline_docs_id: generatedId }));
      }
    }
  };

  const handleFormChange = (field: keyof GuidelineFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = (): boolean => {
    if (!selectedFile) {
      setError('Please select a file');
      return false;
    }

    if (!formData.guideline_docs_id || !formData.title || !formData.description ||
        !formData.source_org || !formData.author || !formData.version_label ||
        !formData.url) {
      setError('Please fill in all required fields');
      return false;
    }

    return true;
  };

  const pollTaskStatus = async (taskId: string): Promise<PipelineTaskStatus['result']> => {
    while (true) {
      try {
        const response = await guidelinePipelineApi.getTaskStatus(taskId);
        const data = response.data;

        // data.progress may be a string or an object like {step, detail}
        const rawProgress = data.progress as any;
        const progressStr = typeof rawProgress === 'string'
          ? rawProgress
          : rawProgress
            ? (rawProgress.detail ?? rawProgress.step ?? JSON.stringify(rawProgress))
            : `Status: ${data.status}`;
        setProgress(progressStr);

        if (data.status === 'SUCCESS') {
          return data.result;
        } else if (data.status === 'FAILURE' || data.status === 'REVOKED') {
          throw new Error(data.error || 'Pipeline processing failed');
        }

        // Wait 3 seconds before polling again
        await new Promise(resolve => setTimeout(resolve, 3000));
      } catch (err: any) {
        if (err.message) throw err;
        throw new Error('Failed to check task status');
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setError(null);
      const guidelineName = formData.title.replace(/[^a-zA-Z0-9]/g, '_');

      setStage('uploading');
      const isPdf = selectedFile!.name.toLowerCase().endsWith('.pdf');
      setProgress(isPdf ? 'Uploading PDF...' : 'Uploading file...');

      let pathPayload: { ocr_json_path?: string; file_uri?: string };
      if (isPdf) {
        const uploadResponse = await guidelinePipelineApi.uploadPdf(selectedFile!, guidelineName);
        pathPayload = { file_uri: uploadResponse.data.file_uri };
      } else {
        const uploadResponse = await guidelinePipelineApi.uploadJson(selectedFile!, guidelineName);
        pathPayload = { ocr_json_path: uploadResponse.data.ocr_json_path };
      }

      // Submit to pipeline
      setStage('pipeline_submitting');
      setProgress('Submitting to pipeline...');

      const submitPayload = {
        guideline_name: guidelineName,
        ...pathPayload,
        skip_summarisation: true,
        upload_to_backend: true,
        doc_payload: {
          guideline_docs_id: formData.guideline_docs_id || undefined,
          title: formData.title,
          description: formData.description,
          source_org: formData.source_org,
          author: formData.author,
          language: formData.language,
          version_label: formData.version_label,
          published_date: formData.published_date,
          image_url: formData.image_url || null,
          url: formData.url,
          jurisdiction: formData.jurisdiction,
          scope: formData.scope.toLowerCase(),
        },
      };

      const submitResponse = await guidelinePipelineApi.submitPipeline(submitPayload);

      const newTaskId = submitResponse.data.task_id;
      setTaskId(newTaskId);

      // Wait for the pipeline to finish creating the guideline before
      // proceeding, so we know it actually exists before assigning it.
      setStage('pipeline_processing');
      setProgress('Processing guideline...');
      await pollTaskStatus(newTaskId);

      // Automatically add the new guideline to the Infiniwell (superadmin)
      // organisation — this should be the only organisation it's auto-assigned to.
      setProgress('Adding guideline to Infiniwell...');
      await guidelinesApi.assignToOrg(formData.guideline_docs_id, {
        organisation_id: INFINIWELL_ORG_ID,
      });

      onSuccess();
      handleClose();
      return;

    } catch (err: any) {
      console.error('Failed to create guideline:', err);
      setStage('error');
      setError(err.response?.data?.detail || err.message || 'Failed to create guideline');
    }
  };

  const handleClose = () => {
    if (stage !== 'idle' && stage !== 'complete' && stage !== 'error') {
      if (!window.confirm('Processing is in progress. Are you sure you want to cancel?')) {
        return;
      }
    }

    setSelectedFile(null);
    setFormData({
      guideline_docs_id: '',
      title: '',
      description: '',
      source_org: '',
      author: '',
      language: 'en',
      version_label: '',
      published_date: new Date().toISOString().split('T')[0],
      image_url: '',
      url: '',
      jurisdiction: 'USA',
      scope: 'National',
    });
    setStage('idle');
    setProgress('');
    setError(null);
    setTaskId(null);
    onClose();
  };

  const isProcessing = stage !== 'idle' && stage !== 'complete' && stage !== 'error';
  const isComplete = stage === 'complete';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Guideline</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isProcessing && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {progress}
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {isComplete && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Guideline created successfully!
          </Alert>
        )}

        {/* File Upload */}
        <Box sx={{ mb: 3 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            disabled={isProcessing}
            fullWidth
          >
            {selectedFile ? selectedFile.name : 'Upload JSON or PDF'}
            <VisuallyHiddenInput
              type="file"
              accept=".json,.pdf"
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Supported formats: JSON, PDF (OCR will be applied for PDF)
          </Typography>
        </Box>

        {/* Document Metadata Form */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Guideline ID"
              value={formData.guideline_docs_id}
              onChange={(e) => handleFormChange('guideline_docs_id', e.target.value)}
              required
              disabled={isProcessing}
              helperText="Auto-generated UUID (can be customized)"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Title"
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              required
              disabled={isProcessing}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              multiline
              rows={3}
              required
              disabled={isProcessing}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Source Organization"
              value={formData.source_org}
              onChange={(e) => handleFormChange('source_org', e.target.value)}
              required
              disabled={isProcessing}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Author"
              value={formData.author}
              onChange={(e) => handleFormChange('author', e.target.value)}
              required
              disabled={isProcessing}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small" disabled={isProcessing}>
              <InputLabel>Language</InputLabel>
              <Select
                value={formData.language}
                label="Language"
                onChange={(e) => handleFormChange('language', e.target.value)}
              >
                <MenuItem value="en">🇺🇸 English</MenuItem>
                <MenuItem value="no">🇳🇴 Norwegian</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Version"
              value={formData.version_label}
              onChange={(e) => handleFormChange('version_label', e.target.value)}
              required
              disabled={isProcessing}
              placeholder="e.g., 2024"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Published Date"
              value={formData.published_date}
              onChange={(e) => handleFormChange('published_date', e.target.value)}
              required
              disabled={isProcessing}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small" disabled={isProcessing}>
              <InputLabel>Jurisdiction</InputLabel>
              <Select
                value={formData.jurisdiction}
                label="Jurisdiction"
                onChange={(e) => handleFormChange('jurisdiction', e.target.value)}
              >
                <MenuItem value="USA">USA</MenuItem>
                <MenuItem value="EU">EU</MenuItem>
                <MenuItem value="UK">UK</MenuItem>
                <MenuItem value="Global">Global</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small" disabled={isProcessing}>
              <InputLabel>Scope</InputLabel>
              <Select
                value={formData.scope}
                label="Scope"
                onChange={(e) => handleFormChange('scope', e.target.value)}
              >
                {SCOPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Source URL"
              value={formData.url}
              onChange={(e) => handleFormChange('url', e.target.value)}
              required
              disabled={isProcessing}
              placeholder="https://..."
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Image URL (optional)"
              value={formData.image_url}
              onChange={(e) => handleFormChange('image_url', e.target.value)}
              disabled={isProcessing}
              placeholder="https://..."
            />
          </Grid>

        </Grid>

        {taskId && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Task ID: {taskId}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing && !isComplete} variant="ghost">
          {isComplete ? 'Close' : 'Cancel'}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isProcessing || isComplete || !selectedFile}
          startIcon={isProcessing ? <CircularProgress size={16} /> : null}
        >
          {isProcessing ? 'Processing...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGuidelineDialog;
